import { useEffect, useState } from 'react';
import { FunnelIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { laufwerkAusschluesse, laufwerkAusschluesseSetzen } from '../../api/client';

/** Karte „Ausschlüsse": Standard-Muster (fest, nur lesend) und eigene Muster (bearbeiten, speichern). */
export default function AusschluesseKarte({ flash }: { flash: (msg: string, variant?: 'ok' | 'err') => void }) {
  const [standard, setStandard] = useState<string[]>([]);
  const [gespeichert, setGespeichert] = useState<string[]>([]);
  const [eigene, setEigene] = useState<string[]>([]);
  const [neu, setNeu] = useState('');
  const [laedt, setLaedt] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let weg = false;
    laufwerkAusschluesse()
      .then((a) => { if (!weg) { setStandard(a.standard); setGespeichert(a.eigene); setEigene(a.eigene); } })
      .catch((e: any) => flash(e?.message ?? String(e), 'err'))
      .finally(() => { if (!weg) setLaedt(false); });
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const geaendert = JSON.stringify(eigene) !== JSON.stringify(gespeichert);

  const hinzu = (e: React.FormEvent) => {
    e.preventDefault();
    const m = neu.trim();
    if (!m) return;
    if (m.length > 200) { flash('Ein Muster darf höchstens 200 Zeichen lang sein.', 'err'); return; }
    if (standard.includes(m)) { flash('Dieses Muster gehört schon zu den Standard-Ausschlüssen.', 'err'); return; }
    if (!eigene.includes(m)) setEigene([...eigene, m]);
    setNeu('');
  };

  const speichern = async () => {
    setBusy(true);
    try {
      const r = await laufwerkAusschluesseSetzen(eigene);
      setGespeichert(r.eigene); setEigene(r.eigene);
      flash('Ausschlüsse gespeichert. Der Connector übernimmt sie beim nächsten Scan.');
    } catch (e: any) { flash(e?.message ?? String(e), 'err'); }
    finally { setBusy(false); }
  };

  return (
    <div className="card space-y-4">
      <div>
        <h2 className="page-h1 flex items-center gap-2"><FunnelIcon className="h-5 w-5 text-ps-dark-400" /> Ausschlüsse</h2>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ps-dark-400">
          Dateien und Ordner, die nicht durchsucht werden. Muster wie <code className="rounded bg-ps-dark-100 px-1">*.bak</code>;
          ein Ordner endet mit einem Schrägstrich, z. B. <code className="rounded bg-ps-dark-100 px-1">Privat/</code>.
        </p>
      </div>

      {laedt ? (
        <div className="space-y-2"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>
      ) : (
        <>
          <div>
            <h3 className="mb-1.5 text-sm font-semibold text-ps-dark-800">Standard <span className="font-normal text-ps-dark-400">(immer aktiv)</span></h3>
            {standard.length === 0 ? <div className="text-xs text-ps-dark-400">Keine.</div> : (
              <div className="flex flex-wrap gap-1.5">
                {standard.map((m) => <span key={m} className="badge badge-neutral break-all font-mono">{m}</span>)}
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-1.5 text-sm font-semibold text-ps-dark-800">Eigene Muster</h3>
            {eigene.length === 0 ? <div className="mb-2 text-xs text-ps-dark-400">Noch keine eigenen Muster.</div> : (
              <ul className="mb-3 divide-y divide-ps-dark-100 rounded-lg border border-ps-dark-200">
                {eigene.map((m) => (
                  <li key={m} className="flex items-center justify-between gap-2 px-3 py-1.5">
                    <span className="min-w-0 break-all font-mono text-sm text-ps-dark-800">{m}</span>
                    <button type="button" onClick={() => setEigene(eigene.filter((x) => x !== m))} aria-label={`Muster ${m} entfernen`}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ps-dark-400 hover:bg-rose-50 hover:text-rose-700">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <form onSubmit={hinzu} className="flex gap-2">
              <input value={neu} onChange={(e) => setNeu(e.target.value)} placeholder="z. B. *.tmp oder Archiv/" aria-label="Neues Muster"
                className="input-field min-w-0 flex-1 font-mono" maxLength={200} />
              <button type="submit" disabled={!neu.trim()} className="btn-outline flex shrink-0 items-center gap-1 text-sm"><PlusIcon className="h-4 w-4" /> Hinzufügen</button>
            </form>
          </div>

          <div className="flex items-center justify-end gap-3">
            {geaendert && <span className="text-xs text-amber-800">Nicht gespeicherte Änderungen</span>}
            <button onClick={() => void speichern()} disabled={busy || !geaendert} className="btn-primary text-sm">{busy ? 'Speichere …' : 'Speichern'}</button>
          </div>
        </>
      )}
    </div>
  );
}
